'use strict';
/* global window */

var _isFunction = require('lodash/isFunction');
var nativeApi = require('./native-api');

exports.getGA = getGA;
exports.isInitialized = isInitialized;
exports.trackEvent = trackEvent;
exports.trackPageview = trackPageview;
exports.setCustomDimensions = setCustomDimensions;
exports.requestUAScript = requestUAScript;
exports.gaCommand = gaCommand;
exports.initGlobalClickListener = initGlobalClickListener;

var BUTTON_LEFT = 0;
var BUTTON_MIDDLE = 1;

var _initialized = false;
var _globalName = 'ga';

function noop() {}

var logInfo = function () {
    if (typeof window !== 'undefined' && window.console && window.console.info) {
        window.console.info.apply(console, arguments);
    }
};

var logWarn = function () {
    if (typeof window !== 'undefined' && window.console && window.console.warn) {
        window.console.warn.apply(console, arguments);
    }
};

// get nearest parent element matching selector
// http://stackoverflow.com/questions/15329167/closest-ancestor-matching-selector-using-native-dom
function closest(el, selector, stop) {
    var matchesSelector = el.matches || el.webkitMatchesSelector || el.mozMatchesSelector || el.msMatchesSelector;
    var element = el;
    while (element) {
        if (matchesSelector.call(element, selector)) {
            break;
        } else if (stop && element === stop) {
            return null;
        }
        element = element.parentElement;
    }
    return element;
}

/**
 * Add event delegation to the given element.
 *
 * @param root The root element for delegation
 * @param event The event name to delegate
 * @param callback The callback function, will be passed the native event as the first
 *     argument, and the target element matching the selector as the second
 * @param selector The selector to match for the event
 */
function delegate(root, event, callback, selector) {
    root.addEventListener(event, function (e) {
        var element = closest(e.target, selector, e.currentTarget);
        if (element) {
            callback(e, element);
        }
    });
}

/**
 * Check the node for the given data attribute. If the attribute exists
 * with the value of '!inherit', then it will search up the DOM until it
 * finds a parent with the data attribute.
 *
 * @method _getDataAttribute
 * @private
 * @param {Node} node The node to search
 * @param {String} attr The data attribute to find, e.g. 'za-label'
 * @param {String} [defaultVal] A default value for the attribute
 * @return {String} The data attribute value, or undefined
 */
function _getDataAttribute(node, attr, defaultVal) {
    var attrName = 'data-' + attr,
        attrValue = defaultVal,
        ancestor;
    if (node && node.hasAttribute(attrName)) {
        attrValue = node.getAttribute(attrName);
    }
    if (attrValue === '!inherit') {
        ancestor = closest(node.parentElement, '[' + attrName + ']');
        return _getDataAttribute(ancestor, attr);
    }
    return attrValue;
}

/**
 * Get the category from data attributes.
 *
 * @method _getCategory
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The category string
 */
function _getCategory(node) {
    return _getDataAttribute(node, 'za-category');
}

/**
 * Get the action from data attributes.
 *
 * @method _getAction
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The action string
 */
function _getAction(node) {
    return _getDataAttribute(node, 'za-action');
}

/**
 * Get the label from data attributes.
 *
 * @method _getLabel
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The label string
 */
function _getLabel(node) {
    return _getDataAttribute(node, 'za-label');
}

/**
 * Parse the value integer from data attributes.
 *
 * @method _getValue
 * @private
 * @param {Node} node The node to get the value from
 * @return {Int} The value integer, or undefined
 */
function _getValue(node) {
    var value = _getDataAttribute(node, 'za-value');
    return parseInt(value, 10) || undefined;
}

/**
 * Parse the noninteraction value from data attributes.
 *
 * @method _getNoninteraction
 * @private
 * @param {Node} node The node to get the value from
 * @return {Boolean} The noninteraction value, or undefined
 */
function _getNoninteraction(node) {
    var value = _getDataAttribute(node, 'za-noninteraction');
    if (value === 'false') {
        return false;
    } else if (value === 'true') {
        return true;
    } else {
        return undefined;
    }
}

/**
 * Get the href from the data attribute or from anchor.
 *
 * @method _getHref
 * @private
 * @param {Node} node The node to get the value from
 * @return {String} The href value
 */
function _getHref(node) {
    var dataHref = _getDataAttribute(node, 'za-href');
    if (typeof dataHref !== 'undefined' && dataHref !== null) {
        if (dataHref === '!ignore') {
            return '';
        } else {
            return dataHref;
        }
    } else if (node.hasAttribute('href')) {
        return node.getAttribute('href');
    } else {
        return undefined;
    }
}

/**
 * Get ua dimension info from the data attribute of node (RE-8249)
 * Dimension 53: Event Details
 * Dimension 54: Event Content
 *
 * @method _getDimensions
 * @private
 * @param {Node} node The node to get the value from
 * @return {Object} Object with key:value pairs that map to ua dimensions:values
 */
function _getDimensions(node) {
    var dataEventDetailsIndex = '53',
        dataEventDetails = _getDataAttribute(node, 'za-event-details'),
        dataEventContentIndex = '54',
        dataEventContent = _getDataAttribute(node, 'za-event-content'),
        dimensions = {};

    if (dataEventDetails) {
        dimensions[dataEventDetailsIndex] = dataEventDetails;
    }
    if (dataEventContent) {
        dimensions[dataEventContentIndex] = dataEventContent;
    }

    return dimensions;
}

/**
 * Get the appropriate URL target, making sure to check for ctrl/middle
 * clicks that would open links in a new window.
 *
 * @method _getTarget
 * @private
 * @param {Event} e
 * @param {Node} node The node that was clicked
 * @return {String} The URL target, or `undefined` if none is explicitly
 *     defined.
 */
function _getTarget(e, node) {
    var target;
    if (e) {
        if (node) {
            // Data attribute takes precedence over anchor target
            target = _getDataAttribute(node, 'za-target');
            if ((typeof target === 'undefined' || target === null) && node.hasAttribute('target')) {
                target = node.getAttribute('target');
            }
        }

        // Check for click behavior
        if ((e.button === BUTTON_MIDDLE && node.nodeName === 'A') ||
            (e.button === BUTTON_LEFT && e.ctrlKey) ||
            (e.button === BUTTON_LEFT && e.metaKey)) {
                target = '_blank';
        }
    }
    return target;
}

/**
 * In most cases, this function will be called by the global click
 * listener set up by `initGlobalClickListener`.
 * The function can also be called itself in the case
 * where additional logic is required during the click event.
 *
 * The basic idea is to wrap `trackEvent`
 * functionality such that you can track events directly
 * from the DOM. Clicking on an element with class `za-track-event` will
 * trigger the event. You can define `trackEvent`
 * parameters using data attributes on the element, e.g.
 * `data-za-category`, `data-za-action`.
 *
 * Data attributes values are generally defined on the element with the
 * `za-track-event` class, but they can also be configured to inherit
 * the value by using the `!inherit` keyword. When inherit is enabled
 * for an attribute, the value will be pulled from the nearest ancestor
 * that has the same data attribute defined.
 *
 * When the `href` parameter is defined, the URL `target` parameter will
 * be taken directly from the link when applicable or pulled from the
 * data attribute `data-za-target`. In the case that the element was
 * control or middle clicked, the target will automatically be set to
 * `_blank` (middle click will only work for anchor elements).
 *
 * ### HTML Example
 *
 *     <!-- Track event before redirecting. -->
 *     <a href="/mortgage-rates/" class="za-track-event" data-za-category="Mortgages" data-za-action="Rate link click">Mortgage rates</a>
 *
 *     <!-- Does not have to be an anchor! -->
 *     <div data-za-href="/mortgage-rates/" class="za-track-event" data-za-category="Mortgages" data-za-action="Rate link click">Mortgage rates</div>
 *
 *     <!-- Inherit attributes from ancestors. -->
 *     <div data-za-category="Mortgages">
 *         <header data-za-label="Header">
 *             <a href="/mortgage-rates/" class="za-track-event" data-za-category="!inherit" data-za-action="Rate link click" data-za-label="!inherit">Mortgage rates</a>
 *         </header>
 *         <footer data-za-label="Footer">
 *             <a href="/mortgage-rates/" class="za-track-event" data-za-category="!inherit" data-za-action="Rate link click" data-za-label="!inherit">Mortgage rates</a>
 *         </footer>
 *     </div>
 *
 * @method handleTrackEventClick
 * @param {Event} e The click event
 * @param {Node} target The node that was clicked
 */
function handleTrackEventClick(e, target) {
    var event = {
        category: _getCategory(target),
        action: _getAction(target),
        label: _getLabel(target),
        value: _getValue(target),
        noninteraction: _getNoninteraction(target),
        href: _getHref(target),
        dimensions: _getDimensions(target),
        target: _getTarget(e, target)
    };

    if (typeof event.href !== 'undefined' && event.href !== null) {
        e.preventDefault();
    }

    trackEvent(event);
}

/**
 * Check if href is a tel link
 *
 * @method _isTelLink
 * @private
 */
function _isTelLink(href) {
    return (/^tel:/i).test(href);
}

/**
 * Get the global Google Analytics object. This is generally window.ga,
 * but the global may be renamed on domains other than zillow.com. For
 * that reason, it is important to only call UA functions through the
 * object returned by this function.
 *
 * @method getGA
 * @return {Object} The GA global object.
 */
function getGA() {
    return typeof window !== 'undefined' && window[_globalName];
}

/**
 * Test to see if Google Universal Analytics is initialized.
 *
 * @method isInitialized
 */
function isInitialized() {
    return _isFunction(getGA());
}

/**
 * Guarded window.open redirect callback generator.
 *
 * @method _getRedirect
 * @param {Object} config
 * @param {String} config.href Redirect to URL after event fires
 * @param {String} config.target The URL target
 */
function _getRedirect(config) {
    if (typeof window !== 'undefined' && window.open) {
        return window.open.bind(window, config.href, config.target);
    }
    return logInfo.bind(null, 'ga redirect window.open("%s", "%s")', config.href, config.target);
}

/**
 * Default analytics event tracking.
 * This may be overriden.
 *
 * @method _trackEvent
 * @private
 * @param {Object} config
 */
function _trackEvent(config) {
    var googleUAObj = {
        hitType: 'event',
        eventCategory: config.category,
        eventAction: config.action,
        eventLabel: config.label,
        eventValue: config.value,
        nonInteraction: config.noninteraction
    };

    if (config.hitCallback) {
        googleUAObj.hitCallback = config.hitCallback;
    }

    if (isInitialized()) {
        logInfo('Sending event to UA: ', googleUAObj);
        getGA()('send', googleUAObj);
    } else {
        logInfo('Would send event to UA but none found: ', googleUAObj);
    }
}

/**
 * Track a Goggle Analytics event
 * (<a href="https://zwiki.zillowgroup.net/display/uxdev/Google+Analytics">read more about the GA tracking</a>).
 *
 * ### Examples
 *
 *     import { trackEvent } from '@zillow/universal-analytics';
 *
 *     // Track an event
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list'
 *     });
 *
 *     // Track an event and then redirect
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list',
 *         href: 'http://www.zillow.com/mortgage-rates/'
 *     });
 *
 *     // Track an event and open url in a new window
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list',
 *         href: 'http://www.zillow.com/mortgage-rates/',
 *         target: '_blank'
 *     });
 *
 * @method trackEvent
 * @param {Object} config
 * @param {String} config.category Event category
 * @param {String} config.action Event action
 * @param {String} [config.label] Event label
 * @param {Number} [config.value] Event value
 * @param {Boolean} [config.noninteraction] Noninteraction event
 * @param {String} [config.href] Redirect to URL after event fires
 * @param {String} [config.target] The URL target, default: '_self'
 */
function trackEvent(config) {
    // Handle href redirects
    if (config.href) {
        config.target = config.target || '_self';

        // Bind href and target to the open function so it can be used
        // with setTimeout.
        var redirect = _getRedirect(config);

        // Not necessary to wait for URLs opened in a new target, and
        // waiting will cause the URL to be opened in a window instead
        // of a tab because it is no longer "user-initiated".
        if (config.target === '_blank' || _isTelLink(config.href)) {
            redirect.call();

        // When GA is intitialized, the redirect will occur when the
        // hit callback is called (usually very fast). If for some
        // reason GA fails or is really slow, the timeout will
        // guarantee the redirect occurs in a timely manner.
        } else if (isInitialized()) {
            var timeout = setTimeout(redirect, 500);
            config.hitCallback = function () {
                // Make sure to clear timeout as anchor hrefs do not
                // change the page.
                clearTimeout(timeout);
                redirect.call();
            };

        // Old school redirect in case GA hasn't been initialized
        } else {
            setTimeout(redirect, 100);
        }
    }

    // Category and action are required fields
    if (typeof config.category === 'undefined') {
        logWarn('trackEvent: missing category');
    } else if (typeof config.action === 'undefined') {
        logWarn('trackEvent: missing action');
    } else {
        if (nativeApi.shouldDispatchToNative()) {
            nativeApi.trackEvent(config, config.hitCallback);
        }
        else {
            _trackEvent(config);
        }
    }
}

/**
 * Track a Google Analytics page view.
 *
 * ### Example
 *
 *     import { trackPageview } from '@zillow/universal-analytics';
 *
 *     // Track a page view
 *     trackPageview({
 *         pageURL: '/postings/sign-in/'
 *     });
 *
 * @method trackPageview
 * @param {Object} [config]
 * @param {String} [config.pageURL] Page URL to track metrics under
 */
function trackPageview(config) {
    config = config || {};
    var googleUAObj = {
        hitType: 'pageview',
        page: config.pageURL
    };

    if (isInitialized()) {
        logInfo('Sending event to UA: ', googleUAObj);
        getGA()('send', googleUAObj);
    } else {
        logInfo('Would send event to UA but none found: ', googleUAObj);
    }
}

/**
 * Set custom dimensions
 *
 * ### Example
 *
 *     import { setCustomDimensions } from '@zillow/universal-analytics';
 *
 *     // set some custom dimensions
 *     setCustomDimensions({
 *         dimension5: 'foo',
 *         dimension10: 'bar
 *     });
 *
 * @method setCustomDimensions
 * @param {object} dimensionsObj An object containing the dimensions as keys, and the corresponding values
 */
function setCustomDimensions(dimensionsObj) {
    if (isInitialized()) {
        logInfo('Setting custom dimensions: ', dimensionsObj);
        getGA()('set', dimensionsObj);
    } else {
        logInfo('Would set custom dimensions in UA but UA was not initialized: ', dimensionsObj);
    }
}

/**
 * Manually request and initialize the Universal Analytics JavaScript
 * snippet. This is NOT required if you are using Border.
 *
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/advanced
 *
 * @method requestUAScript
 * @param {String} globalName The global UA object name.
 */
function requestUAScript(globalName) {
    _globalName = globalName || _globalName;
    /*eslint-disable */
    (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
    (i[r].q=i[r].q||[]).push(arguments);};i[r].l=1*new Date();a=s.createElement(o);
    m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m);
    })(window,document,'script','//www.google-analytics.com/analytics.js',_globalName);
    /*eslint-enable */
}

/**
 * Applies any passed arguments to the ga function.
 *
 * @method gaCommand
 */
function gaCommand() {
    if (isInitialized()) {
        getGA().apply(window, [].slice.call(arguments));
    } else {
        logInfo('gaCommand called, but UA was not initialized!');
    }
}

/**
 * Initialize the global click listener. The listener will handle all
 * clicks on elements with the class `za-track-event`.
 *
 * @method initGlobalClickListener
 */
function initGlobalClickListener() {
    if (typeof document !== undefined && !_initialized) {
        delegate(document.body, 'click', handleTrackEventClick, '.za-track-event');
        _initialized = true;
    }
}



//////////////////
// WEBPACK FOOTER
// ./~/@zillow/universal-analytics/lib/index.js
// module id = 372
// module chunks = 1